import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@tasted/utils/supabase';
import { callQuery, callRpc } from '../utils/rpc';
import { writeQueue } from '../utils/writeQueue';
import {
  buildAnswerWrite,
  buildCompleteWineWrite,
  buildFinishWineWrite,
  finishWineWriteId,
  initSoloWrites,
  queuedAnswerWriteIds,
} from '../utils/soloWrites';
import { reportLoss, setSentryParticipant } from '../utils/sentry';
import { onIdle } from '../utils/idle';

const SoloFlowContext = createContext(null);

// LocalStorage keys
const STORAGE_KEYS = {
  SESSION: 'tasted_solo_session',
  PROGRESS: 'tasted_solo_progress',
};
// TAS-87/88: the participant's access token — minted by solo_join, kept on
// this device only, required by every write RPC. Held OUTSIDE the 24h-expiring
// session blob so a returning player can still resume their own row.
const TOKEN_KEY = 'tasted_solo_token';
export const soloToken = {
  get: () => { try { return localStorage.getItem(TOKEN_KEY) || null; } catch { return null; } },
  set: (t) => { try { if (t) localStorage.setItem(TOKEN_KEY, t); } catch { /* ignore */ } },
  clear: () => { try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ } },
};

// Storage helpers
const saveToStorage = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify({ ...data, timestamp: Date.now() }));
  } catch {
    /* private mode / quota — in-memory state still carries this session */
  }
};

const getFromStorage = (key, maxAgeMs = 24 * 60 * 60 * 1000) => {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    const data = JSON.parse(stored);
    if (Date.now() - data.timestamp > maxAgeMs) {
      localStorage.removeItem(key);
      return null;
    }
    return data;
  } catch {
    return null;
  }
};

const clearStorage = () => {
  Object.values(STORAGE_KEYS).forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  });
};

// Flow steps a refreshed tab can be put back into. Each one rebuilds whatever
// it needs from the server on mount (questions, the wine row, the answer key),
// so landing straight on it is safe. 'wine-select' is the fallback for anything
// else — including 'finished', which has nothing left to resume.
const RESUMABLE_STEPS = new Set(['question', 'rating', 'reveal', 'comparison']);

/**
 * Does this tab have a stored step it is going to try to put back?
 *
 * Read synchronously, so the very first render already knows whether the
 * `flowStep` the screens are about to see is a restored fact or the default
 * standing in for one. See `flowRestored`.
 */
const hasResumableProgress = () => {
  const stored = getFromStorage(STORAGE_KEYS.PROGRESS);
  return Boolean(stored?.currentWineId && RESUMABLE_STEPS.has(stored.flowStep));
};

// True when this phone holds a pick for the question that the server has, or
// may still get. The entry is what submitAnswer writes: `selected` is one
// option, or a list of them. A pick whose write was given up on (`failed`, set
// when the queue drops it) does not count: nothing stored it, so the question
// is one the player has still to answer.
const hasLocalAnswer = (entry) => {
  if (!entry || entry.failed) return false;
  const picked = entry.selected;
  return Array.isArray(picked) ? picked.length > 0 : picked != null && picked !== '';
};

// Mark an answer as failed in the STORED progress, for a write that is dropped
// while its wine is not the one in memory. That is the usual case on a reload:
// the queue gives up on what it restored (too old, a token that never came
// back, a refusal) before the resume below has put any wine back, so there is
// no state to mark, and the stored copy is what the resume and
// startWineTasting go on to read. Without the mark that answer would still say
// `pending` and the question would never be asked again.
//
// Only the progress that write belongs to: the same wine and, where the
// progress says whose it is, the same participant. The timestamp is left
// alone; a mark does not make the progress any younger.
const markStoredAnswerFailed = ({ participantId, wineId, questionId }) => {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.PROGRESS);
    if (!raw) return;
    const stored = JSON.parse(raw);
    if (!stored || stored.currentWineId !== wineId) return;
    if (stored.participantId && participantId && stored.participantId !== participantId) return;
    const entry = stored.answers?.[questionId];
    if (!entry || entry.failed) return;
    stored.answers[questionId] = { ...entry, pending: false, failed: true };
    localStorage.setItem(STORAGE_KEYS.PROGRESS, JSON.stringify(stored));
  } catch {
    /* unreadable or unwritable storage: there is nothing stored to mark */
  }
};

// How many wines' question payloads to pre-fetch. A Lite activation carries
// three or four bottles at most; the cap is there so a mis-set-up session with
// a long wine list can't turn the warm-up into a burst of requests.
const QUESTION_WARM_LIMIT = 4;

export function SoloFlowProvider({ children }) {
  // Session state
  const [sessionCode, setSessionCode] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [sessionData, setSessionData] = useState(null);

  // Participant state
  const [participantId, setParticipantId] = useState(null);
  // Set when a restored session is refused by solo_me; Join shows it once.
  const [expiredNotice, setExpiredNotice] = useState(null);
  const [participant, setParticipant] = useState(null);

  // Current tasting state
  const [currentWineId, setCurrentWineId] = useState(null);
  const [currentWine, setCurrentWine] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});

  // Wine queue for multi-wine selection
  const [wineQueue, setWineQueue] = useState([]);
  const [currentWineIndex, setCurrentWineIndex] = useState(0);

  // Session wines and completions
  const [sessionWines, setSessionWines] = useState([]);
  const [completedWines, setCompletedWines] = useState([]);

  // Flow step for current wine tasting
  const [flowStep, setFlowStep] = useState('wine-select');
  // wine-select | question | rating | comparison | reveal | leaderboard

  // Whether `flowStep` can be believed yet.
  //
  // It starts at 'wine-select' and the player's real step arrives a round trip
  // later: the resume below has to read the session, its wines and the wine's
  // questions before it can put anyone back. Every screen guard that redirects
  // on `flowStep` therefore has a window in which it is reading the DEFAULT,
  // not the player — and on a refresh that window is the whole race, because
  // the screen's own chunk comes out of the browser cache in a millisecond
  // while the resume takes as long as venue wifi does.
  //
  // The guard wins that race, and a player refreshing on the reveal was sent
  // to the picker; the picker, seeing every wine already rated, sent them to
  // the end of the game. They finished the tasting having never passed through
  // the place reveal, which is the one screen that publishes them to the TV
  // (migration 251) — so they vanished from the final board. That is the 13
  // October report, and it is why the screens now wait for this.
  //
  // False only while a resume is actually owed. A tab with nothing stored has
  // nothing to wait for and says so from the first render.
  const [flowRestored, setFlowRestored] = useState(() => !hasResumableProgress());

  // Loading states
  const [loading, setLoading] = useState(false);
  // Set when the question load genuinely failed (after retries) rather than
  // "not asked for yet". The old code advanced to the question screen anyway,
  // stranding the player on "Loading question…" with nothing behind it.
  const [questionsError, setQuestionsError] = useState(null);
  // Set when the session itself couldn't be read — distinguishes "no such code"
  // from "we couldn't reach the server", which need different copy and only one
  // of which is worth retrying.
  const [sessionError, setSessionError] = useState(null);
  // Which wine we last tried to load questions for, so a retry knows the target
  // even after state has moved on.
  const pendingWineRef = useRef(null);
  // Bumped by retrySession() to re-run the session load effect.
  const [sessionReloadKey, setSessionReloadKey] = useState(0);
  // The queue's callbacks are registered once, so they can't close over
  // currentWineId. `answers` is keyed by question_id alone and the SAME question
  // is asked of every wine — without this a late-landing verdict for wine 1
  // would overwrite the entry for wine 2's identical question.
  const currentWineIdRef = useRef(null);
  // The answer map for the wine in memory, as of this instant. Written in the
  // same breath as every setAnswers (see changeAnswers), not a render later:
  // startWineTasting reads it both before and after its question load, and a
  // write dropped in between is marked in this map, not in a snapshot.
  const answersRef = useRef({});
  // Whose answers the map holds: the participant who started (or resumed) the
  // wine. Written into the progress blob with them. A second person joining on
  // the same phone must not be shown the first one's wine as half answered:
  // the questions skipped would never be answered under their name.
  const tastingOwnerRef = useRef(null);
  // Resume runs at most once per page load — a later change to sessionWines
  // (a retry, a refetch) must not yank a playing player back to a stored step.
  const resumeAttemptedRef = useRef(false);
  // The question list as last loaded, so resume can clamp a stored index that
  // no longer fits (an admin edited the wine's questions between sessions).
  const loadedQuestionsRef = useRef([]);

  // The one way the answer map changes. The ref moves first and synchronously,
  // so the queue's callbacks and startWineTasting see a drop or a settle at
  // once; `next` is a map, or a function from the current map to one.
  const changeAnswers = useCallback((next) => {
    const value = typeof next === 'function' ? next(answersRef.current) : next;
    answersRef.current = value;
    setAnswers(value);
  }, []);

  // Restore session from localStorage on mount
  useEffect(() => {
    const stored = getFromStorage(STORAGE_KEYS.SESSION);
    if (stored && stored.sessionCode && stored.participantId) {
      setSessionCode(stored.sessionCode);
      setParticipantId(stored.participantId);
    }
  }, []);

  // Bring the durable write queue online and replay anything a previous page
  // load left unsent. This is what makes an answer survive a refresh, a crash,
  // or a walk out of wifi range: the descriptor is already in localStorage, so
  // it is re-attempted here rather than lost with the tab.
  useEffect(() => {
    // When a queued answer finally lands, fold the server's verdict into local
    // state so the UI stops describing it as unsent.
    writeQueue.setOnSuccess((item, data) => {
      if (item?.meta?.kind !== 'answer') return;
      // Only fold the verdict in if it belongs to the wine on screen.
      if (item.meta.wineId !== currentWineIdRef.current) return;
      const qId = item.meta.questionId;
      changeAnswers((prev) => {
        const existing = prev[qId];
        if (!existing) return prev;
        return {
          ...prev,
          [qId]: {
            ...existing,
            isCorrect: Boolean(data?.is_correct),
            pointsEarned: Number(data?.points_earned) || 0,
            pending: false,
          },
        };
      });
    });

    // Refused as `answer is final` (migration 254): the server already holds
    // this player's answer to the question, a different pick from this one,
    // and kept it. Nothing was lost, so nothing is reported and the answer is
    // neither pending nor failed. The refusal carries no verdict; the
    // comparison screen reads the stored pick and its marking from the key.
    writeQueue.setOnSettle((item) => {
      if (item?.meta?.kind !== 'answer') return;
      if (item.meta.wineId !== currentWineIdRef.current) return;
      changeAnswers((prev) => {
        const existing = prev[item.meta.questionId];
        if (!existing) return prev;
        return {
          ...prev,
          [item.meta.questionId]: { ...existing, pending: false, failed: false },
        };
      });
    });

    // A refused write (question not in this tasting, a first answer for a wine
    // already rated) can't be fixed by retrying. Mark the answer so the UI can
    // be honest about it instead of showing a "saving…" state that will never
    // resolve.
    writeQueue.setOnDrop((item, error) => {
      // Every drop is reported, not just the one the screen can show. A
      // `complete` or `finish` that never lands is a score the board will
      // never carry, and until now it went nowhere anybody would see it.
      reportLoss(
        error?.expired ? 'lite.write_expired'
          : error?.tokenExpired ? 'lite.token_refused'
            : 'lite.write_dropped',
        error,
        { rpc: item?.rpc, kind: item?.meta?.kind, wineId: item?.meta?.wineId, attempts: item?.attempts }
      );
      if (item?.meta?.kind !== 'answer') return;
      if (item.meta.wineId !== currentWineIdRef.current) {
        markStoredAnswerFailed({
          participantId: item.args?.p_participant_id,
          wineId: item.meta.wineId,
          questionId: item.meta.questionId,
        });
        return;
      }
      // `failed` is what gets the question asked again when the wine is
      // resumed (hasLocalAnswer). It reaches the stored progress with the
      // rest of the answer map.
      changeAnswers((prev) => {
        const existing = prev[item.meta.questionId];
        if (!existing) return prev;
        return {
          ...prev,
          [item.meta.questionId]: { ...existing, pending: false, failed: true },
        };
      });
    });

    initSoloWrites({ tokenReader: soloToken.get });
  }, [changeAnswers]);

  // Save session to localStorage when it changes
  useEffect(() => {
    if (sessionCode && participantId) {
      saveToStorage(STORAGE_KEYS.SESSION, { sessionCode, participantId });
    }
  }, [sessionCode, participantId]);

  // So a report says which tasting and which player it belongs to. The id is
  // a uuid, not an address — solo_join's email never reaches Sentry.
  useEffect(() => {
    setSentryParticipant({ participantId, sessionCode });
  }, [participantId, sessionCode]);

  useEffect(() => {
    currentWineIdRef.current = currentWineId;
  }, [currentWineId]);

  // Save progress to localStorage when wine/question changes. `flowStep` rides
  // along because it is the only thing that says WHICH screen the player was on
  // — without it a refresh knows the answers but not where to put them.
  // `participantId` says whose answers they are (see tastingOwnerRef).
  useEffect(() => {
    if (currentWineId && questionIndex >= 0) {
      saveToStorage(STORAGE_KEYS.PROGRESS, {
        currentWineId,
        questionIndex,
        answers,
        flowStep,
        participantId: tastingOwnerRef.current,
      });
    }
  }, [currentWineId, questionIndex, answers, flowStep]);

  // Load session data when sessionCode changes
  useEffect(() => {
    if (!sessionCode) return;

    let cancelled = false;

    const loadSession = async () => {
      setLoading(true);
      setSessionError(null);
      try {
        // Both reads go through callQuery: a hard deadline so they can't hang,
        // and retries so one blip on venue wifi doesn't strand the player on an
        // empty wine list with no way back.
        const { data: session, error, status } = await callQuery((signal) =>
          supabase
            .from('solo_sessions')
            .select('*')
            .eq('code', sessionCode)
            .eq('status', 'active')
            .abortSignal(signal)
            .maybeSingle()
        );
        if (cancelled) return;

        if (error) {
          // A transport failure is NOT "this code doesn't exist". Keep whatever
          // is already on screen, say what actually happened, and let them retry.
          setSessionError(
            status === 0
              ? { kind: 'network', message: "We couldn't reach the server." }
              : { kind: 'unknown', message: error.message || 'Could not load this activation.' }
          );
          setFlowRestored(true);
          return;
        }

        if (!session) {
          setSessionData(null);
          setSessionId(null);
          setSessionWines([]);
          setSessionError({ kind: 'not-found', message: 'That activation code is not active.' });
          // No session, so the resume below will never run. Nothing is going
          // to put a stored step back, and a screen waiting on one would wait
          // for ever — say so.
          setFlowRestored(true);
          return;
        }

        setSessionData(session);
        setSessionId(session.id);

        const { data: wines, error: winesError } = await callQuery((signal) =>
          supabase
            .from('solo_session_wines')
            .select('*, wines_v2(*)')
            .eq('solo_session_id', session.id)
            .order('display_number')
            .abortSignal(signal)
        );
        if (cancelled) return;

        if (winesError) {
          // The session is real but we couldn't list its wines — an empty picker
          // is indistinguishable from "this activation has no wines", so say so
          // and offer the retry rather than showing a plausible-looking blank.
          setSessionError({ kind: 'network', message: "We couldn't load the wines." });
          setFlowRestored(true);
          return;
        }
        setSessionWines(wines || []);
        // An activation with no wines never satisfies the resume's
        // preconditions either.
        if (!wines || wines.length === 0) setFlowRestored(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadSession();
    return () => {
      cancelled = true;
    };
  }, [sessionCode, sessionReloadKey]);

  // Load participant data when participantId changes
  useEffect(() => {
    if (!participantId) return;

    const loadParticipant = async () => {
      try {
        // solo_me: own row + completions, against this device's token (TAS-87).
        // Retried on transport failures — the branch below FORGETS the player's
        // session, so it must only run on a definite answer from the server.
        const { data, error } = await callRpc('solo_me', {
          p_participant_id: participantId,
          p_token: soloToken.get(),
        });

        if (!error && data?.participant) {
          setParticipant(data.participant);
          setCompletedWines((data.completions || []).map((c) => c.wine_id));
        } else if (!error && !data) {
          // No row for this id + token: a pre-125 saved session with no token,
          // or a token that no longer matches. Every write would be refused,
          // so forget the participant now and send them back to join with a
          // reason, rather than letting them play into silent failures.
          // (A network error is NOT this — leave the state alone and retry
          // next time.)
          setParticipantId(null);
          setParticipant(null);
          setCompletedWines([]);
          // Nobody to resume as; the progress blob goes with them below.
          setFlowRestored(true);
          soloToken.clear();
          try { localStorage.removeItem(STORAGE_KEYS.SESSION); localStorage.removeItem(STORAGE_KEYS.PROGRESS); } catch { /* ignore */ }
          setExpiredNotice('Your saved game could not be resumed on this device — join again to continue.');
        }
      } catch (err) {
        console.error('Error loading participant:', err);
      }
    };

    loadParticipant();
  }, [participantId]);

  // solo_wine_questions, memoised per (session, wine).
  //
  // The RPC takes a session and a wine and nothing else — no token, no writes,
  // and never the answer key (TAS-87, migration 126; options are picked
  // server-side, answer_count comes back for multi-select). So its answer is
  // the same whenever it is asked, which means it can be asked before the
  // player needs it.
  //
  // That is worth doing because the lobby's "Pouring your wine…" spinner IS
  // this one call: a single-wine activation — the usual shape for Lite, where
  // the code came off a QR on the table — auto-starts the moment the picker
  // mounts, so the player sat watching a round trip on venue wifi. Warmed
  // while they are choosing an avatar, the lobby already has it.
  //
  // Only a usable answer is kept. A failure is dropped from the cache so the
  // picker's "press Start again" really does go back to the server.
  const questionCacheRef = useRef(new Map());

  const fetchWineQuestions = useCallback(
    (wineId) => {
      // Unreachable in practice — every caller has a session by now — but an
      // empty answer is the same thing the server would have said.
      if (!sessionId || !wineId) {
        return Promise.resolve({ data: [], error: null, status: undefined });
      }
      const cache = questionCacheRef.current;
      const key = `${sessionId}:${wineId}`;
      let pending = cache.get(key);
      if (!pending) {
        pending = callRpc('solo_wine_questions', {
          p_session_id: sessionId,
          p_wine_id: wineId,
        }).then(
          (res) => {
            if (res.error || !Array.isArray(res.data) || res.data.length === 0) {
              cache.delete(key);
            }
            return res;
          },
          (err) => {
            cache.delete(key);
            throw err;
          }
        );
        cache.set(key, pending);
      }
      return pending;
    },
    [sessionId]
  );

  // Load the playable question payload for one wine. Returns true only when the
  // questions are actually in hand — the caller must NOT advance the flow on
  // false. (The old code advanced regardless, which is how players ended up on
  // a "Loading question…" screen that never resolved.)
  //
  // The call itself is memoised per (session, wine) by `fetchWineQuestions`
  // above, which is what lets it be asked BEFORE the player needs it.
  const loadQuestionsForWine = useCallback(async (wineId) => {
    pendingWineRef.current = wineId;
    setQuestionsError(null);

    const { data: qs, error, status } = await fetchWineQuestions(wineId);

    if (error) {
      setQuestionsError(
        status === 0
          ? { kind: 'network', message: "We couldn't load the questions." }
          : { kind: 'unknown', message: error.message || "We couldn't load the questions." }
      );
      return false;
    }

    const list = Array.isArray(qs) ? qs : [];
    if (list.length === 0) {
      // A wine with no questions is a setup problem, not a network one. Say so
      // rather than dropping the player onto an empty screen.
      setQuestionsError({
        kind: 'empty',
        message: 'This wine has no questions set up yet.',
      });
      setQuestions([]);
      return false;
    }

    loadedQuestionsRef.current = list;
    setQuestions(list);
    return true;
  }, [fetchWineQuestions]);

  // Put a refreshed tab back where the player was.
  //
  // The progress blob has been written on every answer since this app shipped
  // and never read once — so a refresh mid-wine dropped the player back at the
  // picker with their local answers gone, and offered them a wine they were
  // already halfway through. The server had the answers; the tab just forgot.
  //
  // Runs once the session's wines are known, because that is what makes the
  // stored wine id verifiable. Everything else is rebuilt from the server:
  // questions are re-fetched, and if THAT fails we deliberately fall back to the
  // picker rather than resuming into a question screen with nothing behind it.
  useEffect(() => {
    if (resumeAttemptedRef.current) return;
    if (!sessionId || !participantId || sessionWines.length === 0) return;
    resumeAttemptedRef.current = true;

    // Every way out of here settles `flowRestored`: a screen waiting on the
    // resume has to be released whether it put the player back or gave up,
    // or it waits for ever.
    const settle = () => setFlowRestored(true);

    const stored = getFromStorage(STORAGE_KEYS.PROGRESS);
    if (!stored?.currentWineId) return settle();
    if (!RESUMABLE_STEPS.has(stored.flowStep)) return settle();
    // Somebody else's wine, left on this phone. A blob from before the field
    // was written carries no name and is resumed as it always was.
    if (stored.participantId && stored.participantId !== participantId) return settle();

    // A wine that isn't in this activation (a stale blob from another session)
    // is not something to resume into.
    const wineRow = sessionWines.find((w) => w.wine_id === stored.currentWineId);
    if (!wineRow) return settle();

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const ok = await loadQuestionsForWine(stored.currentWineId);
        if (cancelled) return;
        if (!ok) {
          // questionsError is already set; the picker explains it and offers
          // the retry. Better a working picker than a dead question screen.
          return;
        }

        // Read the answers again rather than using the copy from before the
        // load: a write dropped meanwhile was marked failed in the stored one
        // (markStoredAnswerFailed). From here the wine is the one in memory,
        // and the ref says so at once, not a render later, so a drop that
        // lands next is marked in the state being set below.
        const latest = getFromStorage(STORAGE_KEYS.PROGRESS);
        const storedAnswers =
          latest?.currentWineId === stored.currentWineId ? latest.answers : stored.answers;
        currentWineIdRef.current = stored.currentWineId;
        tastingOwnerRef.current = participantId;
        setCurrentWineId(stored.currentWineId);
        setCurrentWine(wineRow.wines_v2 || null);
        setWineQueue([stored.currentWineId]);
        setCurrentWineIndex(0);
        changeAnswers(storedAnswers && typeof storedAnswers === 'object' ? storedAnswers : {});
        // Clamp: a stored index past the end would leave `currentQuestion`
        // null, which is the "Loading question…" screen that never resolves.
        const lastIndex = Math.max(0, loadedQuestionsRef.current.length - 1);
        const storedIndex = Number.isInteger(stored.questionIndex) ? stored.questionIndex : 0;
        setQuestionIndex(Math.min(Math.max(0, storedIndex), lastIndex));
        setFlowStep(stored.flowStep);
      } finally {
        if (!cancelled) {
          setLoading(false);
          // After setFlowStep in the same commit, so no screen ever sees the
          // resume released while the step is still the default.
          settle();
        }
      }
    })();

    return () => {
      cancelled = true;
      // Abandoned part-way (the deps changed, or the tab is going). The guard
      // at the top means this will not be tried again, so release the screens
      // rather than leave them waiting on a resume that is no longer coming.
      settle();
    };
  }, [sessionId, participantId, sessionWines, loadQuestionsForWine, changeAnswers]);

  // Warm the question payload for whatever this player is about to taste.
  //
  // Runs as soon as the activation's wines are known — which is while they are
  // still on the join form or the avatar grid — so by the time the lobby asks
  // for them the round trip is already paid for. Serialised and on an idle
  // callback: the screen in front of the player gets the connection first.
  //
  // Untasted wines only, and capped, so a multi-wine activation can't turn
  // this into a burst of requests on a congested room's wifi.
  useEffect(() => {
    if (!sessionId || sessionWines.length === 0) return;
    const upcoming = sessionWines
      .filter((w) => !completedWines.includes(w.wine_id))
      .slice(0, QUESTION_WARM_LIMIT);
    if (upcoming.length === 0) return;

    let cancelled = false;
    const cancelIdle = onIdle(() => {
      // Sequential: one in flight at a time, and stop if the player has moved
      // on to something that needs the connection more.
      upcoming.reduce(
        (chain, wine) =>
          chain.then(() => {
            if (cancelled) return undefined;
            return fetchWineQuestions(wine.wine_id).catch(() => {});
          }),
        Promise.resolve()
      );
    });

    return () => {
      cancelled = true;
      cancelIdle();
    };
  }, [sessionId, sessionWines, completedWines, fetchWineQuestions]);

  // Start tasting wine(s) - accepts single wineId or array of wineIds.
  //
  // Resolves the step the flow entered, 'question' or 'rating', or false when
  // the questions could not be loaded (the caller must not advance on false).
  //
  // A wine this phone already holds answers for is RESUMED, not restarted: an
  // answer is final on the server once it is stored (migration 254), so asking
  // the same questions again can only produce a second pick the server will
  // not take. The player carries on at the first question they have not
  // answered here, and a wine they answered in full goes to its rating.
  //
  // An answer whose write was given up on (`failed`) is not an answer for
  // this: the server may well not hold one, so the question is asked again
  // and the player gets the second try a restart used to give them. If the
  // server does hold one after all, the new pick is refused as final and
  // settled, which loses nothing. submitAnswer then carries on from there to
  // the next question that still needs an answer, not through the ones
  // already answered.
  //
  // "Holds answers for" means this sitting on this phone: the answer map in
  // memory when it is this wine's, else the stored progress the resume effect
  // reads, and in both cases only when they are this participant's. A phone
  // with neither (a different phone after re-admission) starts at question 1.
  // The answers given on the first phone still stand: a different pick is
  // refused as final and settled, and the comparison shows the stored one.
  const startWineTasting = useCallback(
    async (wineIds) => {
      if (!sessionId) return false;

      // Normalize to array
      const wineIdArray = Array.isArray(wineIds) ? wineIds : [wineIds];
      if (wineIdArray.length === 0) return false;

      const firstWineId = wineIdArray[0];

      // Read before anything below replaces it.
      let held = {};
      if (participantId) {
        if (
          tastingOwnerRef.current === participantId &&
          currentWineIdRef.current === firstWineId
        ) {
          held = answersRef.current || {};
        } else {
          const stored = getFromStorage(STORAGE_KEYS.PROGRESS);
          if (
            stored?.currentWineId === firstWineId &&
            stored.participantId === participantId &&
            stored.answers &&
            typeof stored.answers === 'object'
          ) {
            held = stored.answers;
          }
        }
      }

      setLoading(true);
      setWineQueue(wineIdArray);
      setCurrentWineIndex(0);

      tastingOwnerRef.current = participantId;
      // The wine in memory from this line on, for the queue's callbacks too:
      // a write dropped while the questions load is marked in these answers.
      currentWineIdRef.current = firstWineId;
      setCurrentWineId(firstWineId);
      setQuestionIndex(0);
      // Kept even if the load below fails, so pressing Start again still
      // finds them.
      changeAnswers(held);

      try {
        // Load wine details
        const wineData = sessionWines.find((w) => w.wine_id === firstWineId);
        setCurrentWine(wineData?.wines_v2 || null);

        const ok = await loadQuestionsForWine(firstWineId);
        // Only enter the question flow once there is something to answer.
        if (!ok) return false;

        // The first question with no answer on this phone, a failed one
        // included. Read from the answers as they are NOW: a write dropped
        // while the questions loaded was marked failed in them, and `held`
        // still shows it as answered. A settle that landed meanwhile counts
        // the other way. Questions come back in the same order on every load; only
        // the wrong options are picked again (migration 127), and the question
        // screen never shows an earlier pick, so a pick that is no longer
        // among the options (a failed one being asked again, say) is not on
        // screen to go wrong.
        const list = loadedQuestionsRef.current;
        const current = answersRef.current || {};
        const next = list.findIndex((q) => !hasLocalAnswer(current[q.question_id]));
        if (next === -1) {
          // Every question answered and the wine not rated: the rating is all
          // that is left. The index rests on the last question, where the
          // ordinary flow leaves it.
          setQuestionIndex(Math.max(0, list.length - 1));
          setFlowStep('rating');
          return 'rating';
        }
        setQuestionIndex(next);
        setFlowStep('question');
        return 'question';
      } finally {
        setLoading(false);
      }
    },
    [sessionId, participantId, sessionWines, loadQuestionsForWine, changeAnswers]
  );

  /** Re-attempt the question load that failed, for the retry button. */
  const retryLoadQuestions = useCallback(async () => {
    const wineId = pendingWineRef.current || currentWineId;
    if (!wineId) return false;
    setLoading(true);
    try {
      const ok = await loadQuestionsForWine(wineId);
      if (ok) setFlowStep('question');
      return ok;
    } finally {
      setLoading(false);
    }
  }, [currentWineId, loadQuestionsForWine]);

  /** Re-attempt the session/wines load, for the retry button. */
  const retrySession = useCallback(() => {
    setSessionReloadKey((k) => k + 1);
  }, []);

  // Submit answer for current question
  // Returns { isLastQuestion: boolean } to allow caller to handle transitions
  //
  // This used to await the RPC inline and swallow any error — one dropped packet
  // and the answer was gone, with the results screen later reporting "You didn't
  // answer this one". Now the write goes onto the durable queue: it is recorded
  // locally and on disk first, then retried until the server has it, across
  // reconnects and reloads. The UI never waits on the network to advance, so a
  // slow connection can't freeze the question screen either.
  const submitAnswer = useCallback(
    async (selected) => {
      if (!participantId || !currentWineId || !questions[questionIndex]) {
        return { isLastQuestion: false };
      }

      const question = questions[questionIndex];
      const selectedArr = Array.isArray(selected) ? selected : [selected];

      // Graded on the server (TAS-87): solo_submit_answer reads the correct
      // answer from solo_session_questions, applies the same rules as
      // apps/game (1 point per correct pick, x2 on grape varietal), upserts
      // solo_answers, and returns the verdict. Held as `pending` until that
      // verdict comes back — see the queue's onSuccess handler above.
      writeQueue.enqueue(
        buildAnswerWrite({
          participantId,
          token: soloToken.get(),
          wineId: currentWineId,
          questionId: question.question_id,
          selected: selectedArr,
        })
      );

      // Nothing is published from here. solo_submit_answer keeps the player's
      // own total_score current (migration 245). solo_finish_wine is what
      // shows a score to the room, and the room must not see it before the
      // player reaches their place reveal (migration 246), so that call is
      // sent from updateLeaderboardScores and from nowhere else.

      const newAnswers = {
        ...answersRef.current,
        [question.question_id]: {
          selected,
          isCorrect: false,
          pointsEarned: 0,
          pending: true,
        },
      };
      changeAnswers(newAnswers);

      // On to the next question that still needs an answer. On a first pass
      // that is simply the one after this. On a wine resumed at a failed
      // answer (startWineTasting) the ones in between are already answered,
      // and walking through them again would only produce picks the server
      // refuses as final. Forwards only, so every answer moves the player on
      // and the rating is always reached.
      const nextIndex = questions.findIndex(
        (q, i) => i > questionIndex && !hasLocalAnswer(newAnswers[q.question_id])
      );
      // "Last" is "nothing after this needs answering": the caller goes to
      // the rating.
      const isLastQuestion = nextIndex === -1;

      if (!isLastQuestion) {
        setQuestionIndex(nextIndex);
      }
      // Don't auto-advance on last question - let caller handle transition

      return { isLastQuestion, newAnswers };
    },
    [participantId, currentWineId, questions, questionIndex, changeAnswers]
  );

  // Update leaderboard scores after all questions are answered.
  //
  // Order matters: the server recomputes the total by SUMMING solo_answers, so
  // any answer still sitting in the queue would simply not be counted. Flushing
  // first (answers drain ahead of this write by priority) is what stops a
  // reconnecting player from being short-changed on the board.
  const updateLeaderboardScores = useCallback(
    async () => {
      if (!participantId || !currentWineId || !sessionId) return false;

      // Drain the ANSWERS FIRST, then queue the recompute. Priority ordering
      // alone isn't enough: the queue processes one write at a time, so a score
      // write enqueued while an answer is mid-flight could still be picked up
      // first and sum a set that's one answer short. Flushing before enqueueing
      // removes the ordering assumption entirely.
      const answersLanded = await writeQueue.flush(6000);

      // The venue TV lists a player once the server has seen this write AFTER
      // the wine's rating (`complete`). The descriptor says so itself: it is
      // queued `after` the rating, so while that write is still waiting,
      // retrying or parked, this one is held behind it and goes the moment
      // the rating has landed.
      //
      // Cancel, then enqueue. Builds before this one queued the same id after
      // every answer, without `after`, and enqueueing over an id keeps the OLD
      // slot, ahead of `complete`. A tab that reloads into this build can still
      // have one of those in storage; dropping it puts this one behind the
      // rating in queue order as well. The write is a recompute, so nothing is
      // lost by the swap.
      writeQueue.cancel(finishWineWriteId(participantId, currentWineId));
      writeQueue.enqueue(
        buildFinishWineWrite({
          participantId,
          token: soloToken.get(),
          wineId: currentWineId,
        })
      );

      // Bounded: if the player is offline the write stays queued and lands
      // later — we show the board as it stands rather than hanging on it.
      const drained = answersLanded && (await writeQueue.flush(6000));

      if (drained) {
        // Read the authoritative total back so the UI matches the board.
        const { data, error } = await callRpc('solo_me', {
          p_participant_id: participantId,
          p_token: soloToken.get(),
        });
        if (!error && data?.participant) {
          setParticipant(data.participant);
          // Same read, same truth: a completion the server already holds must
          // not be missing locally (a reload can leave the list short).
          if (Array.isArray(data.completions) && data.completions.length > 0) {
            const done = data.completions.map((c) => c.wine_id);
            setCompletedWines((prev) => [...new Set([...prev, ...done])]);
          }
        }
      }
      return drained;
    },
    [participantId, currentWineId, sessionId]
  );

  /**
   * Make sure the board has been told about every wine this player has rated.
   *
   * The TV lists a finisher only once solo_finish_wine has arrived for every
   * wine in the activation, and it counts a call as that wine's reveal only
   * when the wine's rating is already recorded (migration 251). Exactly one
   * screen sends that call: the place reveal, through updateLeaderboardScores.
   * So a player who reaches the end of the tasting without passing through it
   * for some wine is complete, correctly scored, and missing from the final
   * board — which is what a refresh during the reveal did on 13 October.
   *
   * The guards no longer send anyone past the reveal (see flowRestored), but
   * "they reached the end, so they revealed every wine" was never a safe thing
   * to infer from one screen's mount. This is the backstop, called from the
   * end of the game: every rated wine gets its call, whether or not the reveal
   * sent one.
   *
   * Re-sending one the board already has costs nothing. The server keeps the
   * revealed wines as a SET and writes finished_at once, so a replay adds
   * nothing and cannot push anyone down the board. The descriptor's id is
   * per (participant, wine), so enqueueing over one already waiting supersedes
   * it rather than queueing a second.
   */
  const publishRevealedWines = useCallback(
    (wineIds) => {
      if (!participantId || !Array.isArray(wineIds)) return;
      const token = soloToken.get();
      [...new Set(wineIds)].forEach((wineId) => {
        if (!wineId) return;
        writeQueue.enqueue(buildFinishWineWrite({ participantId, token, wineId }));
      });
    },
    [participantId]
  );

  // Replace, not merge: the server's list of finished wines becomes the list.
  // `completedWines` is optimistic (a wine counts the moment it is rated, before
  // the write lands), so a rating the queue dropped leaves it claiming a wine
  // the server never saw. The merge in updateLeaderboardScores can only add;
  // the prize screen, when the server refuses a claim as "not every wine", needs
  // the server's own answer to win. Resolves the fresh list of wine ids, or
  // null when it could not be read (nothing is changed then).
  const replaceCompletedWinesFromServer = useCallback(async () => {
    if (!participantId) return null;
    const { data, error } = await callRpc('solo_me', {
      p_participant_id: participantId,
      p_token: soloToken.get(),
    });
    if (error || !data?.participant) return null;
    const done = (data.completions || []).map((c) => c.wine_id);
    setParticipant(data.participant);
    setCompletedWines(done);
    return done;
  }, [participantId]);

  // Complete wine tasting with rating.
  // The wine's points are already added to total_score by updateLeaderboardScores
  // (fired on the last question), so this only records the completion + rating
  // and bumps wines_completed — it must NOT re-add the score or it double counts.
  //
  // The flow now advances UNCONDITIONALLY. Previously setFlowStep('reveal') sat
  // inside the try after the RPC, so a failed write left the player stuck on the
  // rating screen with a dead button — one of the frozen screens reported from
  // the room. The write is durable, so advancing without waiting loses nothing.
  const completeWineTasting = useCallback(
    async (rating) => {
      if (!participantId || !currentWineId || !sessionId) return;

      // One RPC (TAS-87): completion row (rating + server-summed wine score),
      // wines_completed and total_score all recomputed server-side.
      //
      // Queued `after` every answer for this wine that has not left the queue
      // yet, parked ones included. The server refuses a FIRST answer for a wine
      // that is already rated (migration 254), and a drain passes over a
      // parked answer, so without this a rating could overtake an answer that
      // was only waiting for signal and cost the player that question. An
      // answer that leaves the queue any way at all (stored, settled as final,
      // dropped) stops holding the rating, so it cannot wait for ever. The
      // reveal's finish write waits on this one in turn (buildFinishWineWrite).
      writeQueue.enqueue(
        buildCompleteWineWrite({
          participantId,
          token: soloToken.get(),
          wineId: currentWineId,
          rating,
          after: queuedAnswerWriteIds(participantId, currentWineId),
        })
      );

      // Optimistic locally — the server recomputes both numbers from
      // solo_answers when the write lands, and solo_me refreshes them after.
      setCompletedWines((prev) =>
        prev.includes(currentWineId) ? prev : [...prev, currentWineId]
      );

      // Rating is the last step before the big reveal.
      setFlowStep('reveal');
    },
    [participantId, currentWineId, sessionId]
  );

  // Reset to wine selection
  const returnToWineSelect = useCallback(() => {
    setCurrentWineId(null);
    setCurrentWine(null);
    setQuestions([]);
    setQuestionIndex(0);
    changeAnswers({});
    setWineQueue([]);
    setCurrentWineIndex(0);
    setFlowStep('wine-select');
    // Clear progress from storage
    try {
      localStorage.removeItem(STORAGE_KEYS.PROGRESS);
    } catch {
      /* ignore */
    }
  }, [changeAnswers]);

  // Clear all session data (logout)
  const clearSession = useCallback(() => {
    setSessionCode(null);
    setSessionId(null);
    setSessionData(null);
    setParticipantId(null);
    setParticipant(null);
    setCurrentWineId(null);
    setCurrentWine(null);
    setQuestions([]);
    setQuestionIndex(0);
    changeAnswers({});
    setWineQueue([]);
    setCurrentWineIndex(0);
    setSessionWines([]);
    setCompletedWines([]);
    setFlowStep('wine-select');
    setQuestionsError(null);
    setSessionError(null);
    clearStorage();
    soloToken.clear();
    // Queued writes are signed with the old token — replaying them after a
    // logout can only be refused, so drop them rather than leaving unsendable
    // work parked in storage forever.
    writeQueue.clear();
  }, [changeAnswers]);

  // Check if a wine is completed
  const isWineCompleted = useCallback(
    (wineId) => completedWines.includes(wineId),
    [completedWines]
  );

  // Get current question
  const currentQuestion = questions[questionIndex] || null;

  // Progress percentage for current wine
  const questionProgress =
    questions.length > 0 ? ((questionIndex + 1) / questions.length) * 100 : 0;

  const value = {
    // Session
    sessionCode,
    setSessionCode,
    sessionId,
    sessionData,
    // Participant
    participantId,
    expiredNotice,
    clearExpiredNotice: () => setExpiredNotice(null),
    setParticipantId,
    participant,
    setParticipant,
    // Current tasting
    currentWineId,
    setCurrentWineId,
    currentWine,
    questions,
    questionIndex,
    setQuestionIndex,
    currentQuestion,
    answers,
    questionProgress,
    // Load failures, surfaced so pages can offer a retry instead of a dead
    // "Loading…" that never resolves.
    questionsError,
    retryLoadQuestions,
    sessionError,
    retrySession,
    // The wine "queue" only ever holds the one wine the player picked. Multi-wine
    // was scaffolded (startWineTasting still accepts an array) but no screen ever
    // advanced it, so the advance/among-many helpers that used to be exported
    // here were removed rather than left looking available.
    wineQueue,
    currentWineIndex,
    // Flow
    flowStep,
    setFlowStep,
    // Whether `flowStep` is the player's or still the default. Any guard that
    // redirects on the step has to wait for this.
    flowRestored,
    sessionWines,
    completedWines,
    replaceCompletedWinesFromServer,
    publishRevealedWines,
    // Actions
    startWineTasting,
    submitAnswer,
    completeWineTasting,
    returnToWineSelect,
    clearSession,
    isWineCompleted,
    updateLeaderboardScores,
    // Loading
    loading,
  };

  return (
    <SoloFlowContext.Provider value={value}>
      {children}
    </SoloFlowContext.Provider>
  );
}

export function useSoloFlow() {
  const context = useContext(SoloFlowContext);
  if (!context) {
    throw new Error('useSoloFlow must be used within SoloFlowProvider');
  }
  return context;
}

export default SoloFlowContext;
